import { COLORS } from '@btime/constants'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { SmallText, Text } from '../../../../../components/presentation/Text'
import { SubItem } from '../../../../../components/structure/SubItems'
import { getQueryParam } from '../../../../../utils/helpers/url'
import { useChecklistFormActions, useSelectedQuestion } from '../../_form/ChecklistFormHandler'
import DirectChildren from './DirectChildren'
import Draggable from '../../../../../components/dnd/Draggable'
import Droppable from '../../../../../components/dnd/Droppable'
import FlexGrid, { FlexCol } from '../../../../../components/structure/FlexGrid'
import Link from '../../../../../components/actions/Link'
import LogicControl from './LogicControl'
import Panel from '../../../../../components/structure/Panel'
import QuestionInfoTags from './QuestionInfoTags'
import QuestionMenu from './menu/QuestionMenu'
import RecordReference from '../../../../../components/presentation/RecordReference'
import SeparatorWrapper from '../../../../../components/helpers/SeparatorWrapper'
import dndTypes from '../_constants/dndTypes'

const QuestionPanel = React.memo(
  ({
    question,
    categoryIndex,
    index,
    selectQuestion,
    addQuestionCloseTo,
    moveElementCloseTo,
    isSelected,
    isSubItem,
    displayConditions
  }) => {
    const { t } = useTranslation('editChecklist')
    const slimView = getQueryParam('slim') === 'true'
    const subtitle = t('questionNumber', { number: index + 1 })
    const title = question.name

    const onDrop = useCallback((item, dir) => {
      if (!item.reference) {
        addQuestionCloseTo(question.reference, { ...item, displayConditions }, dir)
      } else {
        moveElementCloseTo(item.reference, question.reference, dir)
      }
    })

    return (
      <div>
        <SubItem disabled={!isSubItem}>
          <Droppable type={dndTypes.QUESTION} onDrop={onDrop}>
            <QuestionMenu open={isSelected} questionId={question?.id} />
            <Draggable type={dndTypes.QUESTION} item={question} hideOnDrag>
              <Panel lineBorder noMargin noPadding noRadius borderLeftColor={isSelected && COLORS.purple}>
                <SeparatorWrapper usePadding weight={!slimView ? 5 : 1}>
                  <FlexGrid justify="space-between" gutter={0.2}>
                    <FlexCol flex={1}>
                      <Link onClick={() => selectQuestion(question.reference)} block>
                        {!slimView && (
                          <SmallText color={COLORS.dark3} size="xxsmall">
                            {subtitle}
                          </SmallText>
                        )}
                        <Text bold={!!title} color={!!title ? COLORS.dark2 : COLORS.dark3} size={slimView && 'small'}>
                          {title || t('addQuestionLabel')}
                        </Text>
                        {!slimView && <RecordReference valuePrefix=":reply:ref" record={question} />}
                      </Link>
                    </FlexCol>

                    <QuestionInfoTags question={question} />
                  </FlexGrid>
                </SeparatorWrapper>
              </Panel>
            </Draggable>
          </Droppable>
        </SubItem>
        <LogicControl question={question} />
        <DirectChildren question={question} />
      </div>
    )
  }
)

// To improve performance the hooks are called in this Wrapper
// and the main component wrapped in memo() so it will not rerender
// on every state change
const Wrapper = ({ question, ...props }) => {
  const { selectQuestion, moveElementCloseTo, addQuestionCloseTo } = useChecklistFormActions()
  const selectedQuestion = useSelectedQuestion()
  const isSelected = selectedQuestion.reference === question.reference

  return (
    <QuestionPanel
      {...props}
      addQuestionCloseTo={addQuestionCloseTo}
      question={question}
      isSelected={isSelected}
      selectQuestion={selectQuestion}
      moveElementCloseTo={moveElementCloseTo}
    />
  )
}

export default Wrapper
